import React, { useEffect, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Popover,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { CalendarBlank, Columns } from '@phosphor-icons/react';
import api from '../../utils/api';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CustomCalendarHeader from '../Calendar/CustomCalendarHeader';
import DayShiftsModal from './DayShiftsModal';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addMonths, addDays, eachDayOfInterval, isSameDay, isSameMonth } from 'date-fns';
import { getStaffBackgroundColor, getStaffBorderColor, getStaffBackgroundColorWithAlpha } from '../../utils/staffColors';
import { getStaffMemberDisplayPhoto } from '../../utils/avatarConstants';

function AllStaffCalendar({ businessId, onBackToGrid }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const calendarRef = useRef(null);
  const lastRangeRef = useRef({ start: null, end: null });
  const [shiftEvents, setShiftEvents] = useState([]);
  const [timeOffEvents, setTimeOffEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [eventPopover, setEventPopover] = useState({
    anchorEl: null,
    title: '',
    time: ''
  });
  const [dayModal, setDayModal] = useState({ open: false, date: null });
  const [mobileViewMode, setMobileViewMode] = useState('week');
  const [mobileWeekStart, setMobileWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [mobileMonthStart, setMobileMonthStart] = useState(() => startOfMonth(new Date()));

  const resolveTeamMemberId = (teamMember) => {
    if (!teamMember) return '';
    return teamMember._id?.toString?.() || teamMember._id || teamMember.toString?.() || '';
  };

  const getStaffDisplayInfo = (teamMember, staffDirectory = {}) => {
    const teamMemberId = resolveTeamMemberId(teamMember);
    const directoryStaff = teamMemberId ? staffDirectory[teamMemberId] : null;
    const source = directoryStaff || teamMember || {};
    const sourceUser = source.user || {};
    const teamMemberUser = teamMember?.user || {};
    const firstName = sourceUser.firstName || teamMemberUser.firstName || '';
    const lastName = sourceUser.lastName || teamMemberUser.lastName || '';

    if (!source && !teamMember) {
      return {
        fullName: 'Staff',
        avatar: null,
        initials: 'ST'
      };
    }

    const fullName = `${firstName} ${lastName}`.trim() || source.name || teamMember?.name || 'Staff';
    const avatar = getStaffMemberDisplayPhoto({
      user: source.user || teamMember?.user,
      profileImage: source.profileImage || teamMember?.profileImage
    })
      || source.profilePicture
      || source.avatar
      || source.image
      || sourceUser.profileImage
      || teamMemberUser.profileImage
      || null;
    const nameParts = fullName.split(' ').filter(Boolean);
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
      : fullName.slice(0, 2).toUpperCase();

    return { fullName, avatar, initials };
  };

  const buildEventTimeLabel = (event) => {
    if (event.allDay || event.extendedProps?.type === 'timeOff') {
      return 'All day';
    }
    const startLabel = event.start ? format(event.start, 'h:mm a') : '';
    const endLabel = event.end ? format(event.end, 'h:mm a') : '';
    return startLabel && endLabel ? `${startLabel} - ${endLabel}` : startLabel || endLabel || 'Time not set';
  };

  const loadEvents = async (viewInfo) => {
    if (!businessId) return;
    if (!viewInfo || !viewInfo.startStr || !viewInfo.endStr) {
      return;
    }
    
    const start = viewInfo.startStr;
    const end = viewInfo.endStr;

    // Avoid duplicate fetches for the same range
    if (lastRangeRef.current.start === start && lastRangeRef.current.end === end) {
      return;
    }
    lastRangeRef.current = { start, end };
    
    try {
      setLoading(true);
      setError('');

      // Fetch all shifts and schedules for the business (cache-busted)
      const ts = Date.now();
      const [shiftsRes, schedulesRes, staffRes] = await Promise.all([
        api.get(
          `/api/team-member-shifts/business/${businessId}`,
          {
            headers: { 'Cache-Control': 'no-cache' },
            params: {
              start,
              end,
              _: ts
            }
          }
        ),
        api.get(
          `/api/team-member-schedules/business/${businessId}`,
          { headers: { 'Cache-Control': 'no-cache' } }
        ),
        api.get(
          `/api/supplier/staff-members?businessId=${businessId}`,
          { headers: { 'Cache-Control': 'no-cache' } }
        )
      ]);

      const staffDirectory = (staffRes.data?.staffMembers || []).reduce((acc, staffMember) => {
        if (staffMember?._id) {
          acc[staffMember._id.toString()] = staffMember;
        }
        return acc;
      }, {});

      // Process shifts - each shift has teamMember populated with StaffMember info
      const shifts = shiftsRes.data?.shifts || [];

      const shiftEvents = shifts.map((shift) => {
        const teamMember = shift.teamMember;
        const { fullName, avatar, initials } = getStaffDisplayInfo(teamMember, staffDirectory);

        // Get staff-specific colors
        const staffColors = teamMember ? {
          backgroundColor: getStaffBackgroundColor(teamMember),
          borderColor: getStaffBorderColor(teamMember)
        } : {
          backgroundColor: theme.palette.primary.light,
          borderColor: theme.palette.primary.main
        };

        const event = {
          id: `shift-${shift._id}`,
          title: `${fullName}'s Shift`,
          start: shift.startTime,
          end: shift.endTime,
          ...staffColors,
          extendedProps: {
            type: 'shift',
            teamMemberId: shift.teamMember?._id,
            teamMemberName: fullName,
            teamMemberAvatar: avatar,
            teamMemberInitials: initials
          }
        };
        return event;
      });

      // Process time off from all schedules
      // Filter time off to only include entries that overlap with the view range
      const viewStart = new Date(start);
      const viewEnd = new Date(end);
      const timeOffEvents = [];
      (schedulesRes.data?.schedules || []).forEach((schedule) => {
        const teamMember = schedule.teamMember;
        const { fullName, avatar, initials } = getStaffDisplayInfo(teamMember, staffDirectory);
        
        // Get staff-specific colors for time off (using transparent version)
        const staffColors = teamMember ? {
          backgroundColor: getStaffBackgroundColorWithAlpha(teamMember, 0.6), // Semi-transparent
          borderColor: getStaffBorderColor(teamMember),
          borderStyle: 'dashed' // Dashed border for time off
        } : {
          backgroundColor: theme.palette.error.light,
          borderColor: theme.palette.error.main
        };
        
        (schedule.timeOff || []).forEach((off) => {
          const offStart = new Date(off.startDate);
          const offEnd = new Date(off.endDate);
          
          // Only include if time off overlaps with view range
          if (offEnd >= viewStart && offStart <= viewEnd) {
            const reason = off.reason === 'time_off' ? 'Day Off' : (off.reason || 'Day Off');
            
            // For time off, make it an all-day event for proper display in month view
            // End date should be exclusive (next day) for FullCalendar
            const endDate = new Date(offEnd);
            endDate.setDate(endDate.getDate() + 1);
            
            timeOffEvents.push({
              id: `timeoff-${off._id}`,
              title: `${fullName}'s ${reason}`,
              start: off.startDate,
              end: endDate.toISOString(),
              allDay: true,
              ...staffColors,
              display: 'block',
              extendedProps: {
                type: 'timeOff',
                teamMemberId: schedule.teamMember?._id,
                teamMemberName: fullName,
                teamMemberAvatar: avatar,
                teamMemberInitials: initials
              }
            });
          }
        });
      });

      // Only update events if we have valid data (don't clear on empty results from bad date ranges)
      setShiftEvents(shiftEvents);
      setTimeOffEvents(timeOffEvents);
    } catch (err) {
      setError('Failed to load calendar events');
      // Don't clear events on error - keep what we have
    } finally {
      setLoading(false);
    }
  };

  // Load events when businessId changes or calendar mounts (desktop) or mobile range changes
  useEffect(() => {
    if (!businessId) return;

    if (isMobile) {
      const start = mobileViewMode === 'week'
        ? startOfWeek(mobileWeekStart, { weekStartsOn: 1 })
        : startOfMonth(mobileMonthStart);
      const end = mobileViewMode === 'week'
        ? endOfWeek(mobileWeekStart, { weekStartsOn: 1 })
        : endOfMonth(mobileMonthStart);
      loadEvents({
        startStr: start.toISOString(),
        endStr: end.toISOString()
      });
      return;
    }

    // Desktop: wait for FullCalendar to be ready
    const timer = setTimeout(() => {
      const calendar = calendarRef.current?.getApi();
      if (calendar && calendar.view && calendar.view.activeStart && calendar.view.activeEnd) {
        loadEvents({
          startStr: calendar.view.activeStart.toISOString(),
          endStr: calendar.view.activeEnd.toISOString()
        });
      } else {
        const retryTimer = setTimeout(() => {
          const cal = calendarRef.current?.getApi();
          if (cal?.view) {
            loadEvents({
              startStr: cal.view.activeStart.toISOString(),
              endStr: cal.view.activeEnd.toISOString()
            });
          }
        }, 300);
        return () => clearTimeout(retryTimer);
      }
    }, 100);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId, isMobile, mobileViewMode, mobileWeekStart?.toISOString?.(), mobileMonthStart?.toISOString?.()]);

  return (
    <Card sx={isMobile ? { overflow: 'visible' } : undefined}>
      <CardContent sx={isMobile ? { overflow: 'visible' } : undefined}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6">Staff Schedule</Typography>
            <Typography variant="body2" color="text.secondary">
              View all staff shifts and time off
            </Typography>
          </Box>
          {onBackToGrid && (
            <Button
              variant="outlined"
              onClick={onBackToGrid}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              Go back
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {isMobile ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, position: 'relative', overflow: 'visible', minWidth: 0 }}>
            {loading && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <ToggleButtonGroup
              value={mobileViewMode}
              exclusive
              onChange={(_, v) => v && setMobileViewMode(v)}
              size="small"
            >
              <ToggleButton value="week" aria-label="Week view">
                <Columns size={18} style={{ marginRight: 6 }} />
                Week
              </ToggleButton>
              <ToggleButton value="calendar" aria-label="Calendar view">
                <CalendarBlank size={18} style={{ marginRight: 6 }} />
                Calendar
              </ToggleButton>
            </ToggleButtonGroup>

            {mobileViewMode === 'week' ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => setMobileWeekStart(d => addDays(d, -7))} sx={{ minWidth: 44, minHeight: 44 }}>
                    <NavigateBefore fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setMobileWeekStart(d => addDays(d, 7))} sx={{ minWidth: 44, minHeight: 44 }}>
                    <NavigateNext fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {format(mobileWeekStart, 'd MMM')} – {format(endOfWeek(mobileWeekStart, { weekStartsOn: 1 }), 'd MMM yyyy')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', overflowX: 'auto', gap: 1, pb: 1, minHeight: 0, minWidth: 0, width: '100%', WebkitOverflowScrolling: 'touch' }}>
                  {eachDayOfInterval({ start: mobileWeekStart, end: endOfWeek(mobileWeekStart, { weekStartsOn: 1 }) }).map((day) => {
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const allEvents = [...shiftEvents, ...timeOffEvents];
                    const dayEvents = allEvents.filter((e) => {
                      const es = new Date(e.start);
                      const ee = new Date(e.end);
                      const ds = new Date(day);
                      ds.setHours(0, 0, 0, 0);
                      const de = new Date(day);
                      de.setHours(23, 59, 59, 999);
                      return es < de && ee > ds;
                    });
                    return (
                      <Box
                        key={dayKey}
                        sx={{
                          flex: '0 0 120px',
                          minWidth: 120,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: 'action.hover',
                            cursor: 'pointer',
                            minHeight: 44,
                            touchAction: 'manipulation',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:active': { bgcolor: 'action.selected' }
                          }}
                          onClick={() => setDayModal({ open: true, date: day })}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {format(day, 'EEE')}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {format(day, 'd')}
                          </Typography>
                          {dayEvents.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}>
                              {[...Array(Math.min(3, dayEvents.length))].map((_, i) => (
                                <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ p: 1, flex: 1, overflow: 'auto', maxHeight: 200 }}>
                          {dayEvents.slice(0, 5).map((event) => (
                            <Box
                              key={event.id}
                              sx={{
                                p: 0.75,
                                mb: 0.5,
                                borderRadius: 1,
                                backgroundColor: event.backgroundColor || 'action.hover',
                                border: '1px solid',
                                borderColor: event.borderColor || 'divider',
                                minHeight: 36
                              }}
                            >
                              <Typography variant="caption" fontWeight={600} display="block" noWrap>
                                {event.extendedProps?.teamMemberName || event.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {buildEventTimeLabel(event)}
                              </Typography>
                            </Box>
                          ))}
                          {dayEvents.length > 5 && (
                            <Typography variant="caption" color="text.secondary">
                              +{dayEvents.length - 5} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <IconButton size="small" onClick={() => setMobileMonthStart(d => addMonths(d, -1))} sx={{ minWidth: 44, minHeight: 44 }}>
                    <NavigateBefore fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {format(mobileMonthStart, 'MMMM yyyy')}
                  </Typography>
                  <IconButton size="small" onClick={() => setMobileMonthStart(d => addMonths(d, 1))} sx={{ minWidth: 44, minHeight: 44 }}>
                    <NavigateNext fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, '& > *': { aspectRatio: '1', minHeight: 40 } }}>
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                    <Typography key={d + i} variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', py: 0.5 }}>
                      {d}
                    </Typography>
                  ))}
                  {(() => {
                    const monthStart = startOfMonth(mobileMonthStart);
                    const monthEnd = endOfMonth(mobileMonthStart);
                    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
                    const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
                    const allEvents = [...shiftEvents, ...timeOffEvents];
                    return calDays.map((d) => {
                      const hasEvents = allEvents.some((e) => {
                        const es = new Date(e.start);
                        const ee = new Date(e.end);
                        const ds = new Date(d);
                        ds.setHours(0, 0, 0, 0);
                        const de = new Date(d);
                        de.setHours(23, 59, 59, 999);
                        return es < de && ee > ds;
                      });
                      const isCurrentMonth = isSameMonth(d, monthStart);
                      const isToday = isSameDay(d, new Date());
                      return (
                        <Box
                          key={d.toISOString()}
                          onClick={() => setDayModal({ open: true, date: d })}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                            minHeight: 44,
                            bgcolor: isToday ? 'primary.main' : 'transparent',
                            color: isToday ? 'primary.contrastText' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                            '&:active': { bgcolor: isToday ? 'primary.dark' : 'action.selected' }
                          }}
                        >
                          <Typography variant="body2" fontWeight={isToday ? 700 : 400}>
                            {format(d, 'd')}
                          </Typography>
                          {hasEvents && (
                            <Box sx={{ display: 'flex', gap: 0.25, mt: 0.25 }}>
                              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: isToday ? 'primary.contrastText' : 'primary.main' }} />
                            </Box>
                          )}
                        </Box>
                      );
                    });
                  })()}
                </Box>
              </>
            )}
          </Box>
        ) : (
        <Box 
          sx={{ 
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            '& .fc-button-primary': {
              backgroundColor: '#E91E63 !important',
              borderColor: '#C2185B !important',
              color: '#fff !important',
              '&:hover': {
                backgroundColor: '#C2185B !important',
                borderColor: '#AD1457 !important',
              },
              '&:focus': {
                backgroundColor: '#C2185B !important',
                borderColor: '#AD1457 !important',
                boxShadow: '0 0 0 0.2rem rgba(233, 30, 99, 0.25) !important',
              },
            },
            '& .fc-button-active': {
              backgroundColor: '#AD1457 !important',
              borderColor: '#880E4F !important',
              '&:hover': {
                backgroundColor: '#880E4F !important',
                borderColor: '#6A0D3A !important',
              },
            },
            '& .fc-button:disabled': {
              backgroundColor: '#F8BBD0 !important',
              borderColor: '#F48FB1 !important',
              color: '#fff !important',
              opacity: 0.6,
            },
            // White background for calendar
            backgroundColor: '#ffffff',
            '& .fc-scrollgrid': {
              backgroundColor: '#ffffff',
            },
            '& .fc-daygrid-day': {
              backgroundColor: '#ffffff',
            },
            '& .fc-timegrid-col': {
              backgroundColor: '#ffffff',
            },
            '& .fc-timegrid-slot': {
              backgroundColor: '#ffffff',
              // Keep horizontal lines - removed borderTop: none
            },
            '& .fc-timegrid-slot-lane': {
              // Keep horizontal lines - removed borderTop: none
            },
            // Event text styling - black
            '& .fc-event-title': {
              color: '#000000 !important',
            },
            '& .fc-event-time': {
              color: '#000000 !important',
            },
            '& .fc-event': {
              '& *': {
                color: '#000000 !important',
              }
            },
            '& .fc-timegrid-event .fc-event-main': {
              padding: '2px 4px',
            },
            '& .fc-timegrid-event .fc-event-main-frame': {
              gap: '1px',
            },
            '& .fc-timegrid-event .fc-event-time': {
              fontSize: '0.7rem !important',
              fontWeight: 700,
              lineHeight: 1.15,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .fc-timegrid-event .fc-event-title': {
              fontSize: '0.72rem !important',
              fontWeight: 600,
              lineHeight: 1.15,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            },
            '& .fc-timegrid-event.fc-event-short .fc-event-main-frame': {
              flexDirection: 'column',
            },
            '& .fc-timegrid-event.fc-event-short .fc-event-time': {
              fontSize: '0.66rem !important',
              fontWeight: 700,
            },
            // Table headers text-align left
            '& .fc-col-header-cell': {
              fontSize: '0.875rem',
              fontWeight: 400,
              padding: '8px 4px',
              textAlign: 'left !important',
            },
            // Day header styling - two-line format
            '& .fc-day-header-content': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              lineHeight: 1.2,
              textAlign: 'left !important',
            },
            '& .fc-day-header-dayname': {
              fontSize: '0.875rem !important',
              fontWeight: '400 !important',
              color: 'rgba(0, 0, 0, 1) !important', // Black for all day names
            },
            '& .fc-day-header-dayname.fc-day-header-today': {
              color: '#4caf50 !important', // Green for today
            },
            '& .fc-day-header-date': {
              fontSize: '0.75rem !important',
              fontWeight: '400 !important',
              color: 'rgba(0, 0, 0, 0.6) !important', // Grey for date
              marginTop: '2px',
            },
            // Mobile: larger touch targets for day cells and time slots
            [theme.breakpoints.down('lg')]: {
              '& .fc-daygrid-day': {
                minHeight: 56,
              },
              '& .fc-daygrid-day-frame': {
                minHeight: 56,
              },
              '& .fc-timegrid-slot': {
                minHeight: 48,
              },
              '& .fc-timegrid-event': {
                minHeight: 44,
                touchAction: 'manipulation',
              },
            },
          }}
        >
          <CustomCalendarHeader
            calendarRef={calendarRef}
            currentView={currentView}
            onViewChange={(newView) => {
              setCurrentView(newView);
              const calendar = calendarRef.current?.getApi();
              if (calendar) {
                calendar.changeView(newView);
              }
            }}
            dateRange={dateRange}
            showViewSelector={true}
          />
          <FullCalendar
          key={businessId}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={isMobile ? 'timeGridWeek' : 'dayGridMonth'}
          headerToolbar={false}
          height={isMobile ? 420 : 'auto'}
          slotDuration={isMobile ? '01:00:00' : '00:30:00'}
          events={[...shiftEvents, ...timeOffEvents]}
          datesSet={(viewInfo) => {
            loadEvents(viewInfo);
            setDateRange({
              start: viewInfo.start,
              end: viewInfo.end,
              view: viewInfo.view.type
            });
            setCurrentView(viewInfo.view.type);
          }}
          eventDisplay="block"
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
          firstDay={1}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          weekends={true}
          editable={false}
          selectable={false}
          selectMirror={false}
          dayMaxEvents={true}
          moreLinkClick="popover"
          eventColor={theme.palette.primary.main}
          eventContent={(eventInfo) => {
            const event = eventInfo.event;
            const props = event.extendedProps || {};
            const timeLabel = buildEventTimeLabel(event);
            const avatarSrc = props.teamMemberAvatar;
            const initials = props.teamMemberInitials || 'ST';

            return (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  overflow: 'hidden',
                  px: 0.25
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    lineHeight: 1.15,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {timeLabel}
                </Typography>
                <Avatar
                  src={avatarSrc}
                  sx={{
                    width: 18,
                    height: 18,
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    border: '1px solid rgba(0,0,0,0.12)'
                  }}
                >
                  {initials}
                </Avatar>
              </Box>
            );
          }}
          eventDidMount={(eventInfo) => {
            const timeLabel = buildEventTimeLabel(eventInfo.event);
            const teamMemberName = eventInfo.event.extendedProps?.teamMemberName || eventInfo.event.title;
            eventInfo.el.setAttribute('title', `${teamMemberName}\n${timeLabel}`);
            eventInfo.el.setAttribute('aria-label', `${teamMemberName}. ${timeLabel}`);
          }}
          eventMouseEnter={(mouseEnterInfo) => {
            const event = mouseEnterInfo.event;
            const teamMemberName = event.extendedProps?.teamMemberName || event.title;
            setEventPopover({
              anchorEl: mouseEnterInfo.el,
              title: teamMemberName,
              time: buildEventTimeLabel(event)
            });
          }}
          eventMouseLeave={() => {
            setEventPopover({ anchorEl: null, title: '', time: '' });
          }}
          eventClick={(clickInfo) => {
            const event = clickInfo.event;
            const teamMemberName = event.extendedProps?.teamMemberName || event.title;
            setEventPopover({
              anchorEl: clickInfo.el,
              title: teamMemberName,
              time: buildEventTimeLabel(event)
            });
          }}
          dateClick={(dateClickInfo) => {
            setDayModal({ open: true, date: dateClickInfo.date });
          }}
          dayHeaderContent={(dateInfo) => {
            const dayName = format(dateInfo.date, 'EEEE'); // Full day name
            const dateStr = format(dateInfo.date, 'd MMM'); // Date like "29 Dec"
            
            // Check if today
            const today = new Date();
            const isToday = format(dateInfo.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
            
            // Determine color class - only today gets green, all others black
            let colorClass = 'fc-day-header-weekday';
            if (isToday) {
              colorClass = 'fc-day-header-today';
            }
            
            return {
              html: `<div class="fc-day-header-content">
                <div class="fc-day-header-dayname ${colorClass}">${dayName}</div>
                <div class="fc-day-header-date">${dateStr}</div>
              </div>`
            };
          }}
        />
          {loading && (
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              pointerEvents: 'none'
            }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!loading && shiftEvents.length === 0 && timeOffEvents.length === 0 && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No shifts or time off in this range.
              </Typography>
            </Box>
          )}
        </Box>
        )}
        <Popover
          open={Boolean(eventPopover.anchorEl)}
          anchorEl={eventPopover.anchorEl}
          onClose={() => setEventPopover({ anchorEl: null, title: '', time: '' })}
          disableRestoreFocus={isMobile}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ pointerEvents: 'none' }}
          PaperProps={{
            sx: {
              pointerEvents: 'none'
            }
          }}
        >
          <Box sx={{ p: 1.5, maxWidth: 260 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {eventPopover.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {eventPopover.time}
            </Typography>
          </Box>
        </Popover>

        <DayShiftsModal
          open={dayModal.open}
          onClose={() => setDayModal({ open: false, date: null })}
          date={dayModal.date}
          events={[...shiftEvents, ...timeOffEvents]}
          buildEventTimeLabel={buildEventTimeLabel}
        />
      </CardContent>
    </Card>
  );
}

export default AllStaffCalendar;
